import { useState, useEffect } from 'react';
import { Link, Navigate } from 'react-router-dom';
import axios from 'axios';
import Sidebar from '../components/Sidebar';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Calendar, Users, Camera, AlertTriangle, TrendingUp } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Dashboard({ user }) {
  const [stats, setStats] = useState({
    events: [],
    photographers: [],
    upcomingEvents: []
  });
  const [loading, setLoading] = useState(true);

  const isPhotoEd = user?.team_role === 'photo_editor';

  useEffect(() => {
    if (isPhotoEd) return;
    fetchDashboardData();
  }, [isPhotoEd]);

  if (isPhotoEd) return <Navigate to="/photos" replace />;

  const fetchDashboardData = async () => {
    try {
      const [eventsRes, photographersRes] = await Promise.all([
        axios.get(`${API}/events`),
        axios.get(`${API}/photographers`)
      ]);

      const upcomingEvents = eventsRes.data.filter(e => e.status === 'draft' || e.status === 'active');

      setStats({
        events: eventsRes.data,
        photographers: photographersRes.data,
        upcomingEvents
      });
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex">
      <Sidebar user={user} />
      <div className="main-content flex-1">
        <div className="page-header">
          <h1 className="page-title">Dashboard</h1>
          <p className="page-subtitle">Welcome back, {user?.username}! Here's your overview.</p>
        </div>

        {loading ? (
          <div className="loading-spinner">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500"></div>
          </div>
        ) : (
          <>
            <div className="grid-2 mb-8">
              <Card className="glass-card p-6" data-testid="events-stat-card">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-slate-500 text-sm font-medium mb-1">Total Events</p>
                    <p className="text-3xl font-bold text-slate-900">{stats.events.length}</p>
                    <p className="text-emerald-600 text-sm mt-2 flex items-center gap-1">
                      <TrendingUp className="w-4 h-4" />
                      {stats.upcomingEvents.length} upcoming
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-gradient-to-br from-emerald-100 to-teal-100 rounded-xl flex items-center justify-center">
                    <Calendar className="w-6 h-6 text-emerald-600" />
                  </div>
                </div>
              </Card>

              <Card className="glass-card p-6" data-testid="photographers-stat-card">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-slate-500 text-sm font-medium mb-1">Photographers</p>
                    <p className="text-3xl font-bold text-slate-900">{stats.photographers.length}</p>
                    <p className="text-slate-600 text-sm mt-2">Available for assignment</p>
                  </div>
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-xl flex items-center justify-center">
                    <Users className="w-6 h-6 text-blue-600" />
                  </div>
                </div>
              </Card>
            </div>

            <div className="grid gap-6 lg:grid-cols-2">
              <Card className="glass-card p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-semibold text-slate-900">Upcoming Events</h2>
                  <Link to="/events">
                    <Button variant="ghost" size="sm" className="text-emerald-600" data-testid="view-all-events-btn">
                      View all
                    </Button>
                  </Link>
                </div>
                <div className="space-y-4">
                  {stats.upcomingEvents.length === 0 ? (
                    <div className="text-center py-8 text-slate-500">
                      <Calendar className="w-12 h-12 mx-auto mb-3 opacity-30" />
                      <p>No upcoming events</p>
                      <Link to="/events">
                        <Button className="mt-4 btn-gradient" data-testid="create-first-event-btn">
                          Create Your First Event
                        </Button>
                      </Link>
                    </div>
                  ) : (
                    stats.upcomingEvents.slice(0, 5).map((event) => (
                      <Link key={event.id} to={`/events/${event.id}`} className="block">
                        <div
                          className="p-4 rounded-lg bg-white/50 border border-slate-200 hover:border-emerald-300 hover:bg-white transition-all"
                          data-testid={`event-item-${event.id}`}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-semibold text-slate-900">{event.name}</p>
                              <p className="text-sm text-slate-600 mt-1">
                                {event.date} • {event.client}
                              </p>
                            </div>
                            <span className={`status-badge status-${event.status}`}>
                              {event.status}
                            </span>
                          </div>
                        </div>
                      </Link>
                    ))
                  )}
                </div>
              </Card>

              <Card className="glass-card p-6">
                <h2 className="text-xl font-semibold text-slate-900 mb-6">Quick Actions</h2>
                <div className="space-y-3">
                  <Link to="/events">
                    <Button className="w-full justify-start h-14 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white" data-testid="create-event-btn">
                      <Calendar className="w-5 h-5 mr-3" />
                      Create New Event
                    </Button>
                  </Link>
                  <Link to="/photographers">
                    <Button variant="outline" className="w-full justify-start h-14 border-2" data-testid="manage-photographers-btn">
                      <Users className="w-5 h-5 mr-3" />
                      Manage Photographers
                    </Button>
                  </Link>
                  <Link to="/gear">
                    <Button variant="outline" className="w-full justify-start h-14 border-2" data-testid="manage-gear-btn">
                      <Camera className="w-5 h-5 mr-3" />
                      Manage Gear & Kits
                    </Button>
                  </Link>
                </div>
              </Card>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
